import { shiftDay } from '@iac/contracts';
import type { Locale } from '@iac/contracts';

/** Today lives at the app root so the today nav link stays highlighted. */
export function dayHref(date: string, today: string): string {
  return date === today ? '/calories' : `/calories/days/${date}`;
}

// Quantities keep Polish number formatting in every language, like amounts.
const quantityFormatter = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 2,
  useGrouping: false,
});

/** "2", "1,25" — the way the quantity field takes it back. */
export function formatQuantity(quantity: number): string {
  return quantityFormatter.format(quantity);
}

/** "250 g · 40 kcal/100g", leaving out whichever is unknown. */
export function productHint(p: {
  grams: number | null;
  kcalPer100g: number | null;
}): string {
  return [
    p.grams === null ? null : `${p.grams} g`,
    p.kcalPer100g === null ? null : `${p.kcalPer100g} kcal/100g`,
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

/** "2 × 250 g × 40 kcal/100g"; a missing number shows as "?". */
export function entryDetail(e: {
  grams: number | null;
  kcalPer100g: number | null;
  quantity: number;
}): string {
  const product = `${e.grams ?? '?'} g × ${e.kcalPer100g ?? '?'} kcal/100g`;
  return e.quantity === 1
    ? product
    : `${formatQuantity(e.quantity)} × ${product}`;
}

// Weights too: always one decimal, comma in both languages.
const weightFormatter = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: false,
});

/** "81,2" — the way the weight field takes it back. */
export function formatWeight(kg: number): string {
  return weightFormatter.format(kg);
}

const weekdayFormatters = new Map<Locale, Intl.DateTimeFormat>();

function weekdayFormatter(locale: Locale): Intl.DateTimeFormat {
  let formatter = weekdayFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: 'long',
    });
    weekdayFormatters.set(locale, formatter);
  }
  return formatter;
}

/**
 * "Dziś, niedziela" / "Yesterday, Saturday" / "Jutro, poniedziałek", otherwise
 * just "Środa" — the full date is already in the date picker next to it.
 */
export function dayHeading(
  date: string,
  today: string,
  locale: Locale,
  words: { today: string; yesterday: string; tomorrow: string },
): string {
  const weekday = weekdayFormatter(locale).format(
    new Date(`${date}T00:00:00Z`),
  );
  const near =
    date === today
      ? words.today
      : date === shiftDay(today, -1)
        ? words.yesterday
        : date === shiftDay(today, 1)
          ? words.tomorrow
          : null;
  return near
    ? `${near}, ${weekday}`
    : weekday.charAt(0).toUpperCase() + weekday.slice(1);
}
